import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { CandidateTable } from '../components/candidate-table';
import { RunCountersLine, RunStatusBadge } from '../components/imports';
import { btnGhost } from '../components/ui';
import { ApiError, type CandidateRow, type ImportRun } from '../lib/api';
import { OUTCOME_LABELS, formatStamp } from '../lib/format';
import { useSession } from '../lib/session';

const GROUPS = ['uncertain', 'update', 'new', 'duplicate', 'invalid'] as const;

/** Un'esecuzione: contatori, qualità della lettura e i candidati prodotti, per esito. */
export function RunDetail() {
  const { id } = useParams();
  const { api, canEdit } = useSession();
  const [run, setRun] = useState<ImportRun | null>(null);
  const [rows, setRows] = useState<CandidateRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      const r = await api<{ run: ImportRun; candidates: CandidateRow[] }>(`/imports/runs/${id}`);
      setRun(r.run);
      setRows(r.candidates);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    }
  };
  useEffect(() => {
    if (canEdit) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canEdit]);
  useEffect(() => {
    if (run?.status !== 'running') return;
    const t = setInterval(() => void load(), 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.status]);

  const groups = useMemo(
    () =>
      GROUPS.map((o) => ({ outcome: o, rows: rows.filter((r) => r.outcome === o) })).filter(
        (g) => g.rows.length > 0,
      ),
    [rows],
  );
  const onDone = (row: CandidateRow) =>
    setRows((prev) => prev.map((r) => (r.id === row.id ? row : r)));

  return (
    <main className="mx-auto max-w-[1400px] p-4 md:p-6">
      <Link to="/sources" className={btnGhost}>
        <ArrowLeft className="size-4" aria-hidden /> Sorgenti
      </Link>
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {run && (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="font-display text-h2">
              {run.sourceId} · {formatStamp(run.startedAt)}
            </h1>
            <RunStatusBadge status={run.status} />
            <button onClick={() => void load()} className={`${btnGhost} ml-auto`}>
              <RefreshCw className="size-4" aria-hidden /> Aggiorna
            </button>
          </div>
          <p className="mt-1 text-sm text-muted">
            {run.trigger === 'manual'
              ? `Avviata a mano${run.by ? ` da ${run.by}` : ''}`
              : 'Pianificata'}
            {run.finishedAt && ` · conclusa ${formatStamp(run.finishedAt)}`}
          </p>
          <div className="mt-3">
            <RunCountersLine run={run} />
          </div>
          {Object.keys(run.quality ?? {}).length > 0 && (
            <dl className="mt-3 grid gap-2 text-sm md:grid-cols-3">
              {Object.entries(run.quality).map(([key, q]) => (
                <div key={key} className="border border-surface-strong p-3">
                  <dt className="text-xs text-muted">{key}</dt>
                  <dd>
                    {typeof q.entries === 'number' && `${q.entries} eventi letti · `}
                    {typeof q.coverage === 'number' &&
                      `copertura ${q.coverage.toFixed(3).replace('.', ',')} · `}
                    {typeof q.unrecognized === 'number' &&
                      `${q.unrecognized} blocchi non riconosciuti`}
                    {typeof q.error === 'string' && <span className="text-danger">{q.error}</span>}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {groups.map((g) => (
            <section key={g.outcome} className="mt-8">
              <h2 className="text-label font-medium uppercase tracking-[0.06em]">
                {OUTCOME_LABELS[g.outcome]!.label} ({g.rows.length})
                <span className="ml-2 font-normal normal-case tracking-normal text-muted">
                  {OUTCOME_LABELS[g.outcome]!.hint}
                </span>
              </h2>
              <div className="mt-2">
                <CandidateTable rows={g.rows} api={api} onDone={onDone} />
              </div>
            </section>
          ))}
          {rows.length === 0 && run.status !== 'running' && (
            <p className="mt-6 text-sm text-muted">
              Nessun evento letto: il contenuto era invariato o non c&apos;era niente di nuovo.
            </p>
          )}
        </>
      )}
    </main>
  );
}
